<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { store } from '@/common/root';
  import { DOMAIN_PROPS_KEYS, DOMAIN_PROPS_ACCESS_TYPES } from '@/common/constants/domainProps.constants';
  import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
  import NotFoundError from '@/common/utils/errors/NotFoundError';
  import useAuthStore from '@/common/store/pinia/auth';
  import {
    isAutotestEnvironment
  } from '@/common/telemetry';
  import useTelemetryStore from '@/common/store/pinia/telemetry';

  const showBanner = ref(false);
  const authStore = useAuthStore();
  const telemetryStore = useTelemetryStore();

  const checkConsentNeeded = async (): Promise<void> => {
    if (isAutotestEnvironment() || !telemetryStore.isTelemetryEnvironmentEnabled) {
      return;
    }

    try {
      await store.$takeFirst('domainProps/FETCH_PROP', {
        domain: DEFAULT_DOMAIN_NAME,
        key: DOMAIN_PROPS_KEYS.TELEMETRY
      });
      showBanner.value = false;
    } catch (error: any) {
      if (error instanceof NotFoundError) {
        showBanner.value = true;
      }
    }
  };

  const handleTelemetryConsent = (): Promise<void> => {
    showBanner.value = false;

    const timestamp = new Date().toISOString();
    const userId = authStore.currentUser?.id;

    return store.dispatch('domainProps/SET', {
      key: DOMAIN_PROPS_KEYS.TELEMETRY,
      value: {
        enabled: true,
        timestamp,
        userId
      },
      domain: DEFAULT_DOMAIN_NAME,
      access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
    }).then(() => useTelemetryStore().enable());
  };

  onMounted(() => {
    checkConsentNeeded();
  });
</script>

<template>
  <div
    v-if="showBanner"
    class="telemetry-consent-banner v-telemetry-consent-banner"
  >
    <div class="banner-content pa-24">
      <h3 class="banner-title">
        {{ $t('Product telemetry') }}
      </h3>
      <i18n
        class="banner-text"
        path="We use telemetry to improve system performance and stability, based on our legitimate interest in maintaining and optimizing the product. This includes infrastructure metrics (e.g., CPU, memory, and disk usage), licensing data, and system identifiers. No user content is collected. You can opt out of telemetry reporting in the System settings. Read more in our {0}."
        tag="p"
      >
        <el-link
          href="https://www.virtuozzo.com/legal/privacy-notice/"
          target="_blank"
        >
          {{ $t('Privacy Notice') }}
        </el-link>
      </i18n>
      <el-button
        class="banner-close-button"
        type="text"
        @click="handleTelemetryConsent"
      >
        <el-icon name="i-times--16" />
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .telemetry-consent-banner {
    position: fixed;
    bottom: 24px;
    left: 24px;
    right: 24px;
    background: $av-inversed-primary;
    border: 1px solid $av-fixed-lightest;
    border-radius: $av-border-radius-2;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    z-index: $av-z-index-notifications;
  }

  .banner-content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 auto;
    padding-right: 48px;
  }

  .banner-title {
    margin: 0;
    font-size: $av-font-size-s;
    font-weight: $av-font-weight-bold;
  }

  .banner-text {
    margin: 0;
    font-size: $av-font-size-xs;
    line-height: 1.5;
  }

  .banner-close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 8px;
    min-width: auto;
    height: auto;
  }
</style>
